import { useState } from 'react';
import { useKey } from '../useKey';
import { useUpdateEffect } from '../../private/hooks/useUpdateEffect';

type KeyPressState = [boolean, KeyboardEvent | null];

type UseKeyPressOptions = {
  onKeyDown?: (event: KeyboardEvent) => void;
  onKeyUp?: (event: KeyboardEvent) => void;
};

export const useKeyPress = (
  keyFilter: Parameters<typeof useKey>[0],
  options?: UseKeyPressOptions,
): KeyPressState => {
  const [keyPressState, setKeyPressState] = useState<KeyPressState>([false, null]);
  const { onKeyDown, onKeyUp } = options ?? {};

  useKey(keyFilter, (event: KeyboardEvent) => setKeyPressState([true, event]), {
    eventName: 'keydown',
  });
  useKey(keyFilter, (event: KeyboardEvent) => setKeyPressState([false, event]), {
    eventName: 'keyup',
  });

  // call handlers once per state change (equivalent to useKeyPressOnce behavior)
  useUpdateEffect(() => {
    const [pressed, event] = keyPressState;

    if (!pressed && onKeyUp && event !== null) {
      onKeyUp(event);
    } else if (pressed && onKeyDown && event !== null) {
      onKeyDown(event);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, [keyPressState[0]]); // just track pressed state, not the event object

  return keyPressState;
};
